import { Calendar, Layers, ListTree } from "lucide-react";
import type React from "react";
import type { Ref } from "react";
import { useImperativeHandle, useMemo, useRef } from "react";
import {
  getForecast12MonthBalancesCount,
  getForecast12MonthBalancesGraphql,
} from "@/api/services/forecast12MonthBalanceGraphqlService";
import {
  getForecast12MonthCategoryBalanceDistinctValues,
  getForecast12MonthCategoryBalancesCount,
  getForecast12MonthCategoryBalancesGraphql,
} from "@/api/services/forecast12MonthCategoryBalanceGraphqlService";
import {
  getForecast12MonthTransactionDistinctValues,
  getForecast12MonthTransactionsCount,
  getForecast12MonthTransactionsGraphql,
} from "@/api/services/forecast12MonthTransactionGraphqlService";
import type {
  ColumnConfig,
  ColumnFilters,
} from "@/components/MaximusTableReusable";
import {
  type DrillTarget,
  defineMultiView,
  type MultiViewDefinition,
  MultiViewTable,
  type MultiViewTableHandle,
} from "@/components/tables/MultiViewTable";
import type { SortInput } from "@/components/tables/tableFilters";
import { SCREEN_NAMES } from "@/constants/screenNames";
import {
  type Forecast12MonthDemoRow,
  getDemo12MonthBalanceRows,
  getDemo12MonthCategoryOptions,
  getDemo12MonthCategoryRows,
  getDemo12MonthTransactionOptions,
  getDemo12MonthTransactionRows,
  monthKeyLabel,
} from "@/data/forecast12MonthDemoData";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";
import type { ExportColumn } from "@/lib/exportColumns";
import { cn, formatDate } from "@/lib/utils";
import type { Forecast12MonthBalanceViewNode } from "../../api/models/forecast12MonthBalanceView";
import type { Forecast12MonthCategoryBalanceViewNode } from "../../api/models/forecast12MonthCategoryBalanceView";
import type { Forecast12MonthTransactionViewNode } from "../../api/models/forecast12MonthTransactionView";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

/** The three drill-chained views: Monthly Balances → Categories → Transactions. */
export const BALANCES_VIEW = "12m-balances";
export const CATEGORIES_VIEW = "12m-categories";
export const TRANSACTIONS_VIEW = "12m-transactions";

/** Unified row rendered by all three views. */
export type Forecast12MonthRow = Forecast12MonthDemoRow;

/** Row-scoping filter for a single month, keyed on the view's `MonthKey`. */
export function monthKeyFilter(monthKey: string): ColumnFilters {
  return { monthKey: { value: monthKey, mode: "equals" } };
}

export interface Forecast12MonthTableHandle {
  /**
   * Drill-equivalent navigation driven from outside the table (e.g. the
   * chart's "View Detail" action): switch view, apply a row-scoped filter and
   * record a history entry.
   */
  navigateTo: (viewKey: string, scopingFilters?: ColumnFilters) => void;
}

const BALANCE_SORT_FIELD_MAP: Record<string, string> = {
  monthKey: "MonthKey",
  openingBalance: "OpeningBalance",
  incoming: "Incoming",
  outgoing: "Outgoing",
  netChange: "NetChange",
  closingBalance: "ClosingBalance",
  confidenceScore: "ConfidenceScore",
};

const CATEGORY_SORT_FIELD_MAP: Record<string, string> = {
  monthKey: "MonthKey",
  category: "Category",
  incoming: "Incoming",
  outgoing: "Outgoing",
  netChange: "NetChange",
  transactionCount: "TransactionCount",
  averageConfidenceScore: "AverageConfidenceScore",
};

const TRANSACTION_SORT_FIELD_MAP: Record<string, string> = {
  forecastDate: "ForecastDate",
  monthKey: "MonthKey",
  counterpartyName: "CounterpartyName",
  description: "Description",
  category: "Category",
  direction: "Direction",
  type: "Type",
  grossAmount: "GrossAmount",
  outstandingAmount: "OutstandingAmount",
  dueDate: "DueDate",
  layer: "Layer",
  status: "Status",
  reference: "Reference",
  dataSourceName: "DataSourceName",
};

const BALANCE_FILTER_FIELDS = {
  text: new Set(["monthKey", "contributingLayers"]),
  date: new Set(["date"]),
  number: new Set([
    "openingBalance",
    "closingBalance",
    "incoming",
    "outgoing",
    "netChange",
    "confidenceScore",
  ]),
};

const CATEGORY_FILTER_FIELDS = {
  text: new Set(["monthKey", "category"]),
  number: new Set([
    "incoming",
    "outgoing",
    "netChange",
    "averageConfidenceScore",
  ]),
};

const TRANSACTION_FILTER_FIELDS = {
  text: new Set([
    "monthKey",
    "counterpartyName",
    "description",
    "category",
    "direction",
    "type",
    "layer",
    "status",
    "reference",
  ]),
  multiSelect: new Set(["dataSourceName"]),
  date: new Set(["forecastDate", "dueDate"]),
  number: new Set(["grossAmount", "outstandingAmount"]),
};

const BALANCE_DEFAULT_COLUMNS = [
  { key: "monthKey", defaultVisible: true, defaultOrder: 0 },
  { key: "openingBalance", defaultVisible: true, defaultOrder: 1 },
  { key: "incoming", defaultVisible: true, defaultOrder: 2 },
  { key: "outgoing", defaultVisible: true, defaultOrder: 3 },
  { key: "netChange", defaultVisible: true, defaultOrder: 4 },
  { key: "closingBalance", defaultVisible: true, defaultOrder: 5 },
  { key: "confidenceScore", defaultVisible: true, defaultOrder: 6 },
];

const CATEGORY_DEFAULT_COLUMNS = [
  { key: "monthKey", defaultVisible: true, defaultOrder: 0 },
  { key: "category", defaultVisible: true, defaultOrder: 1 },
  { key: "incoming", defaultVisible: true, defaultOrder: 2 },
  { key: "outgoing", defaultVisible: true, defaultOrder: 3 },
  { key: "netChange", defaultVisible: true, defaultOrder: 4 },
  { key: "transactionCount", defaultVisible: true, defaultOrder: 5 },
  { key: "averageConfidenceScore", defaultVisible: true, defaultOrder: 6 },
];

const TRANSACTION_DEFAULT_COLUMNS = [
  { key: "forecastDate", defaultVisible: true, defaultOrder: 0 },
  { key: "monthKey", defaultVisible: false, defaultOrder: 1 },
  { key: "counterpartyName", defaultVisible: true, defaultOrder: 2 },
  { key: "description", defaultVisible: true, defaultOrder: 3 },
  { key: "category", defaultVisible: true, defaultOrder: 4 },
  { key: "direction", defaultVisible: true, defaultOrder: 5 },
  { key: "type", defaultVisible: true, defaultOrder: 6 },
  { key: "grossAmount", defaultVisible: true, defaultOrder: 7 },
  { key: "outstandingAmount", defaultVisible: true, defaultOrder: 8 },
  { key: "dueDate", defaultVisible: true, defaultOrder: 9 },
  { key: "layer", defaultVisible: true, defaultOrder: 10 },
  { key: "status", defaultVisible: true, defaultOrder: 11 },
  { key: "reference", defaultVisible: false, defaultOrder: 12 },
  { key: "dataSourceName", defaultVisible: true, defaultOrder: 13 },
];

const BALANCE_EXPORT_COLUMN_MAP: Record<string, ExportColumn> = {
  monthKey: { name: "monthkey", displayName: "Month" },
  openingBalance: { name: "openingbalance", displayName: "Opening Balance" },
  incoming: { name: "incoming", displayName: "Incoming" },
  outgoing: { name: "outgoing", displayName: "Outgoing" },
  netChange: { name: "netchange", displayName: "Net Change" },
  closingBalance: { name: "closingbalance", displayName: "Closing Balance" },
  confidenceScore: { name: "confidencescore", displayName: "Confidence Score" },
};

const CATEGORY_EXPORT_COLUMN_MAP: Record<string, ExportColumn> = {
  monthKey: { name: "monthkey", displayName: "Month" },
  category: { name: "category", displayName: "Category" },
  incoming: { name: "incoming", displayName: "Incoming" },
  outgoing: { name: "outgoing", displayName: "Outgoing" },
  netChange: { name: "netchange", displayName: "Net Change" },
  transactionCount: { name: "transactioncount", displayName: "Transactions" },
  averageConfidenceScore: {
    name: "averageconfidencescore",
    displayName: "Avg Confidence",
  },
};

const TRANSACTION_EXPORT_COLUMN_MAP: Record<string, ExportColumn> = {
  forecastDate: { name: "forecastdate", displayName: "Forecast Date" },
  monthKey: { name: "monthkey", displayName: "Month" },
  counterpartyName: { name: "counterpartyname", displayName: "Counterparty" },
  description: { name: "description", displayName: "Description" },
  category: { name: "category", displayName: "Category" },
  direction: { name: "direction", displayName: "Direction" },
  type: { name: "type", displayName: "Type" },
  grossAmount: { name: "grossamount", displayName: "Gross Amount" },
  outstandingAmount: {
    name: "outstandingamount",
    displayName: "Outstanding Amount",
  },
  dueDate: { name: "duedate", displayName: "Due Date" },
  layer: { name: "layer", displayName: "Layer" },
  status: { name: "status", displayName: "Status" },
  reference: { name: "reference", displayName: "Reference" },
  dataSourceName: { name: "datasourcename", displayName: "Data Source" },
};

const BALANCE_DEFAULT_SORT: SortInput[] = [
  { field: "MonthKey", order: "Ascending" },
];
const CATEGORY_DEFAULT_SORT: SortInput[] = [
  { field: "MonthKey", order: "Ascending" },
];
const TRANSACTION_DEFAULT_SORT: SortInput[] = [
  { field: "ForecastDate", order: "Ascending" },
];

const TRANSACTION_DISTINCT_FIELDS = [
  "Category",
  "Direction",
  "Type",
  "Layer",
  "Status",
  "DataSourceName",
];

type FormatCurrency = (value: number, precision: "full") => string;

/** Human-readable label for a raw DB value (`credit_note` → "Credit Note"). */
function formatLabel(raw: string | null | undefined): string {
  if (!raw) return "—";
  return (
    raw
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ") || "—"
  );
}

function statusBadgeVariant(
  status: string,
): "success" | "warning" | "destructive" | "secondary" | "info" {
  switch (status.toLowerCase()) {
    case "settled":
      return "success";
    case "outstanding":
      return "warning";
    case "overdue":
      return "destructive";
    case "unknown":
      return "secondary";
    default:
      return "info";
  }
}

function monthColumn(filterable: boolean): ColumnConfig<Forecast12MonthRow> {
  return {
    key: "monthKey",
    label: "Month",
    sortable: true,
    filterable,
    filterConfig: { type: "text" },
    align: "left",
    width: 140,
    render: (row) => (
      <div className="flex items-center gap-1 text-sm">
        <Calendar className="h-3 w-3 text-muted-foreground" />
        {monthKeyLabel(row.monthKey)}
      </div>
    ),
  };
}

function currencyColumn(
  key: string,
  label: string,
  value: (row: Forecast12MonthRow) => number | null | undefined,
  formatCurrency: FormatCurrency,
  tone?: "positive" | "negative" | "signed",
): ColumnConfig<Forecast12MonthRow> {
  return {
    key,
    label,
    sortable: true,
    filterable: true,
    filterConfig: { type: "number" },
    align: "right",
    width: 140,
    render: (row) => {
      const amount = value(row) ?? 0;
      return (
        <span
          className={cn(
            "text-sm font-semibold",
            tone === "positive" && "text-success-600",
            tone === "negative" && "text-destructive",
            tone === "signed" &&
              (amount >= 0 ? "text-success-600" : "text-destructive"),
          )}
        >
          {formatCurrency(amount, "full")}
        </span>
      );
    },
  };
}

function percentColumn(
  key: string,
  label: string,
  value: (row: Forecast12MonthRow) => number | null | undefined,
): ColumnConfig<Forecast12MonthRow> {
  return {
    key,
    label,
    sortable: true,
    filterable: true,
    filterConfig: { type: "number" },
    align: "right",
    width: 130,
    render: (row) => {
      const score = value(row);
      return score != null ? (
        <span className="text-sm">{Math.round(score)}%</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  };
}

function buildBalanceColumns(
  formatCurrency: FormatCurrency,
  cashTerms: { in: string; out: string },
): ColumnConfig<Forecast12MonthRow>[] {
  return [
    monthColumn(true),
    currencyColumn(
      "openingBalance",
      "Opening Balance",
      (row) => row.openingBalance,
      formatCurrency,
    ),
    currencyColumn(
      "incoming",
      cashTerms.in,
      (row) => row.incoming,
      formatCurrency,
      "positive",
    ),
    currencyColumn(
      "outgoing",
      cashTerms.out,
      (row) => row.outgoing,
      formatCurrency,
      "negative",
    ),
    currencyColumn(
      "netChange",
      "Net",
      (row) => row.netChange,
      formatCurrency,
      "signed",
    ),
    currencyColumn(
      "closingBalance",
      "Closing Balance",
      (row) => row.closingBalance,
      formatCurrency,
    ),
    percentColumn(
      "confidenceScore",
      "Confidence",
      (row) => row.confidenceScore,
    ),
  ];
}

function buildCategoryColumns(
  distinctValues: Record<string, string[]>,
  formatCurrency: FormatCurrency,
  cashTerms: { in: string; out: string },
): ColumnConfig<Forecast12MonthRow>[] {
  return [
    monthColumn(true),
    {
      key: "category",
      label: "Category",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: distinctValues.Category ?? [] },
      align: "left",
      width: 200,
      render: (row) => <span className="text-sm">{row.category ?? "—"}</span>,
    },
    currencyColumn(
      "incoming",
      cashTerms.in,
      (row) => row.incoming,
      formatCurrency,
      "positive",
    ),
    currencyColumn(
      "outgoing",
      cashTerms.out,
      (row) => row.outgoing,
      formatCurrency,
      "negative",
    ),
    currencyColumn(
      "netChange",
      "Net",
      (row) => row.netChange,
      formatCurrency,
      "signed",
    ),
    {
      key: "transactionCount",
      label: "Transactions",
      sortable: true,
      filterable: false,
      align: "right",
      width: 130,
      render: (row) => (
        <span className="text-sm">{row.transactionCount ?? 0}</span>
      ),
    },
    percentColumn(
      "averageConfidenceScore",
      "Avg Confidence",
      (row) => row.averageConfidenceScore,
    ),
  ];
}

function buildTransactionColumns(
  distinctValues: Record<string, string[]>,
  formatCurrency: FormatCurrency,
  forecastTerm: string,
): ColumnConfig<Forecast12MonthRow>[] {
  const options = (field: string) => distinctValues[field] ?? [];
  const labelledOptions = (field: string) =>
    options(field).map((value) => ({ label: formatLabel(value), value }));

  return [
    {
      key: "forecastDate",
      label: `${forecastTerm} Date`,
      sortable: true,
      filterable: true,
      filterConfig: { type: "date-range" },
      align: "left",
      width: 140,
      render: (row) => (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          {row.forecastDate ? formatDate(new Date(row.forecastDate)) : "—"}
        </div>
      ),
    },
    monthColumn(true),
    {
      key: "counterpartyName",
      label: "Counterparty",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 180,
      render: (row) => (
        <span className="text-sm">{row.counterpartyName ?? "—"}</span>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 240,
      render: (row) => (
        <div className="truncate text-sm">{row.description ?? "—"}</div>
      ),
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: options("Category") },
      align: "left",
      width: 150,
      render: (row) => <span className="text-sm">{row.category ?? "—"}</span>,
    },
    {
      key: "direction",
      label: "Direction",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Direction") },
      align: "left",
      width: 120,
      render: (row) => (
        <span className="text-sm">{formatLabel(row.direction)}</span>
      ),
    },
    {
      key: "type",
      label: "Type",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Type") },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{formatLabel(row.type)}</span>,
    },
    currencyColumn(
      "grossAmount",
      "Gross Amount",
      (row) => row.grossAmount,
      formatCurrency,
    ),
    currencyColumn(
      "outstandingAmount",
      "Outstanding",
      (row) => row.outstandingAmount,
      formatCurrency,
    ),
    {
      key: "dueDate",
      label: "Due Date",
      sortable: true,
      filterable: true,
      filterConfig: { type: "date-range" },
      align: "left",
      width: 130,
      render: (row) =>
        row.dueDate ? (
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-3 w-3" />
            {formatDate(new Date(row.dueDate))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "layer",
      label: "Layer",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: options("Layer") },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{row.layer ?? "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Status") },
      align: "left",
      width: 120,
      render: (row) =>
        row.status ? (
          <Badge variant={statusBadgeVariant(row.status)}>
            {formatLabel(row.status)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "reference",
      label: "Reference",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{row.reference ?? "—"}</span>,
    },
    {
      key: "dataSourceName",
      label: "Data Source",
      sortable: true,
      filterable: true,
      filterConfig: {
        type: "multi-select",
        options: options("DataSourceName"),
      },
      align: "left",
      width: 150,
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.dataSourceName ?? "—"}
        </span>
      ),
    },
  ];
}

export interface Forecast12MonthTableProps {
  /**
   * Change to clear the view history (horizon-anchor moves, demo/live switch,
   * forecast-run or data-source resets). Also clears it on mount.
   */
  historyResetKey?: string | number;
  tableRef?: Ref<Forecast12MonthTableHandle>;
}

/**
 * The 12-month forecast table: three drill-chained views over
 * {@link MultiViewTable} — Monthly Balances → Category Balances →
 * Transactions.
 *
 * Every view is a drill member, so the unified view history is active: toggles,
 * row double-click drills and the external `navigateTo` handle all record a
 * history entry, and the Back button restores the previous view together with
 * its scoping filter.
 */
export const Forecast12MonthTable: React.FC<Forecast12MonthTableProps> = ({
  historyResetKey,
  tableRef,
}) => {
  const { formatCurrency } = useFormatting();
  const { cashMovementTerms: cashTerms, getForecastTerm } = useTerminology();
  const forecastTerm = getForecastTerm(true);

  const multiViewRef = useRef<MultiViewTableHandle>(null);

  useImperativeHandle(tableRef, () => ({
    navigateTo: (viewKey: string, scopingFilters?: ColumnFilters) => {
      multiViewRef.current?.setView(viewKey, scopingFilters);
    },
  }));

  const views = useMemo<MultiViewDefinition<Forecast12MonthRow>[]>(
    () => [
      defineMultiView<Forecast12MonthRow, Forecast12MonthBalanceViewNode>({
        key: BALANCES_VIEW,
        title: "Balance Breakdown",
        screenName: SCREEN_NAMES.FORECAST_12M_BALANCES,
        searchPlaceholder: "Search by month...",
        columns: buildBalanceColumns(formatCurrency, cashTerms),
        defaultColumns: BALANCE_DEFAULT_COLUMNS,
        fetchList: (filter, first, after, sort) =>
          getForecast12MonthBalancesGraphql(filter, first, after, sort),
        fetchCount: (filter) => getForecast12MonthBalancesCount(filter),
        mapNode: (node, cursor) => ({
          rowKey: `12m-bal-${node.id}-${cursor}`,
          id: node.id,
          monthKey: node.monthKey,
          date: node.date,
          openingBalance: node.openingBalance,
          closingBalance: node.closingBalance,
          incoming: node.incoming,
          outgoing: node.outgoing,
          netChange: node.netChange,
          confidenceScore: node.confidenceScore,
        }),
        sortFieldMap: BALANCE_SORT_FIELD_MAP,
        defaultSort: BALANCE_DEFAULT_SORT,
        filterFields: BALANCE_FILTER_FIELDS,
        defaultColumnFilters: undefined,
        export: {
          form: "Forecast12MonthBalances",
          fileName: "forecast-12m-balances-export",
          columns: BALANCE_EXPORT_COLUMN_MAP,
        },
        drillMember: true,
        getDemoRows: getDemo12MonthBalanceRows,
        demoSearchMatch: (row, query) =>
          monthKeyLabel(row.monthKey).toLowerCase().includes(query),
      }),
      defineMultiView<
        Forecast12MonthRow,
        Forecast12MonthCategoryBalanceViewNode
      >({
        key: CATEGORIES_VIEW,
        title: "Categories Breakdown",
        screenName: SCREEN_NAMES.FORECAST_12M_CATEGORIES,
        searchPlaceholder: "Search by category...",
        columns: (distinctValues) =>
          buildCategoryColumns(distinctValues, formatCurrency, cashTerms),
        defaultColumns: CATEGORY_DEFAULT_COLUMNS,
        fetchList: (filter, first, after, sort) =>
          getForecast12MonthCategoryBalancesGraphql(filter, first, after, sort),
        fetchCount: (filter) => getForecast12MonthCategoryBalancesCount(filter),
        mapNode: (node, cursor) => ({
          rowKey: `12m-cat-${node.id}-${cursor}`,
          id: node.id,
          monthKey: node.monthKey,
          category: node.category,
          incoming: node.incoming,
          outgoing: node.outgoing,
          netChange: node.netChange,
          transactionCount: node.transactionCount,
          averageConfidenceScore: node.averageConfidenceScore,
        }),
        sortFieldMap: CATEGORY_SORT_FIELD_MAP,
        defaultSort: CATEGORY_DEFAULT_SORT,
        filterFields: CATEGORY_FILTER_FIELDS,
        distinctValueFields: ["Category"],
        fetchDistinctValues: (field) =>
          getForecast12MonthCategoryBalanceDistinctValues(field),
        defaultColumnFilters: undefined,
        export: {
          form: "Forecast12MonthCategoryBalances",
          fileName: "forecast-12m-category-balances-export",
          columns: CATEGORY_EXPORT_COLUMN_MAP,
        },
        drillMember: true,
        getDemoRows: getDemo12MonthCategoryRows,
        getDemoOptions: getDemo12MonthCategoryOptions,
        demoSearchMatch: (row, query) =>
          (row.category ?? "").toLowerCase().includes(query),
      }),
      defineMultiView<Forecast12MonthRow, Forecast12MonthTransactionViewNode>({
        key: TRANSACTIONS_VIEW,
        title: "Transactions Breakdown",
        screenName: SCREEN_NAMES.FORECAST_12M_TRANSACTIONS,
        searchPlaceholder: "Search by description, counterparty or category...",
        columns: (distinctValues) =>
          buildTransactionColumns(distinctValues, formatCurrency, forecastTerm),
        defaultColumns: TRANSACTION_DEFAULT_COLUMNS,
        fetchList: (filter, first, after, sort) =>
          getForecast12MonthTransactionsGraphql(filter, first, after, sort),
        fetchCount: (filter) => getForecast12MonthTransactionsCount(filter),
        mapNode: (node, cursor) => ({
          rowKey: `12m-txn-${node.id}-${cursor}`,
          id: node.id,
          monthKey: node.monthKey,
          forecastDate: node.forecastDate,
          dueDate: node.dueDate,
          counterpartyName: node.counterpartyName,
          description: node.description,
          category: node.category,
          direction: node.direction,
          type: node.type,
          grossAmount: node.grossAmount,
          outstandingAmount: node.outstandingAmount,
          layer: node.layer,
          status: node.status,
          reference: node.reference,
          dataSourceName: node.dataSourceName,
        }),
        sortFieldMap: TRANSACTION_SORT_FIELD_MAP,
        defaultSort: TRANSACTION_DEFAULT_SORT,
        filterFields: TRANSACTION_FILTER_FIELDS,
        distinctValueFields: TRANSACTION_DISTINCT_FIELDS,
        fetchDistinctValues: (field) =>
          getForecast12MonthTransactionDistinctValues(field),
        defaultColumnFilters: undefined,
        export: {
          form: "Forecast12MonthTransactions",
          fileName: "forecast-12m-transactions-export",
          columns: TRANSACTION_EXPORT_COLUMN_MAP,
        },
        drillMember: true,
        getDemoRows: getDemo12MonthTransactionRows,
        getDemoOptions: getDemo12MonthTransactionOptions,
        demoSearchMatch: (row, query) =>
          [row.description, row.counterpartyName, row.category].some((value) =>
            value?.toLowerCase().includes(query),
          ),
      }),
    ],
    [formatCurrency, cashTerms, forecastTerm],
  );

  // Monthly Balances → Categories (scoped to the month), Categories →
  // Transactions (scoped to the category, retaining the month).
  const resolveDrill = (
    row: Forecast12MonthRow,
    fromViewKey: string,
  ): DrillTarget | null => {
    if (!row.monthKey) return null;
    if (fromViewKey === BALANCES_VIEW) {
      return {
        toViewKey: CATEGORIES_VIEW,
        columnFilters: monthKeyFilter(row.monthKey),
      };
    }
    if (fromViewKey === CATEGORIES_VIEW && row.category) {
      return {
        toViewKey: TRANSACTIONS_VIEW,
        columnFilters: {
          ...monthKeyFilter(row.monthKey),
          category: { value: row.category, mode: "equals" },
        },
      };
    }
    return null;
  };

  return (
    <MultiViewTable<Forecast12MonthRow>
      tableRef={multiViewRef}
      views={views}
      initialViewKey={BALANCES_VIEW}
      resolveDrill={resolveDrill}
      resetStateOnViewChange={true}
      historyResetKey={historyResetKey}
      errorMessage={`Failed to load 12-month ${getForecastTerm(false)} data`}
      renderHeaderActions={({ currentViewKey, setView }) => (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setView(
                currentViewKey === CATEGORIES_VIEW
                  ? BALANCES_VIEW
                  : CATEGORIES_VIEW,
              )
            }
          >
            <Layers className="h-4 w-4 mr-1" />
            {currentViewKey === CATEGORIES_VIEW
              ? "View Balances"
              : "View Categories"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setView(
                currentViewKey === TRANSACTIONS_VIEW
                  ? BALANCES_VIEW
                  : TRANSACTIONS_VIEW,
              )
            }
          >
            <ListTree className="h-4 w-4 mr-1" />
            {currentViewKey === TRANSACTIONS_VIEW
              ? "View Balances"
              : "View Transactions"}
          </Button>
        </>
      )}
    />
  );
};
